import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchMyActivities, fetchActivitySummary, fetchMyGoals, updatePerformanceGoal,
  listPerformanceReviews, submitSelfAssessment,
} from '../api';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const PIE_COLORS = ['#22c55e', '#eab308', '#6366f1', '#ef4444', '#3b82f6', '#f97316', '#14b8a6', '#a855f7', '#64748b'];
const GOAL_STATUS_COLORS = {
  NOT_STARTED: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  ACHIEVED: 'bg-green-100 text-green-700',
  MISSED: 'bg-red-100 text-red-600',
  EXCEEDED: 'bg-emerald-100 text-emerald-700',
};
const REVIEW_STATUS_COLORS = {
  PENDING_SELF: 'bg-yellow-100 text-yellow-700',
  PENDING_MGR: 'bg-blue-100 text-blue-700',
  IN_DISCUSSION: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  ACKNOWLEDGED: 'bg-teal-100 text-teal-700',
};

function MyPerformance({ authAxios, showFormMessage, currentUser }) {
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('30'); // days
  const [inlineActual, setInlineActual] = useState({});
  const [showSelfAssess, setShowSelfAssess] = useState(null);
  const [selfAssessData, setSelfAssessData] = useState({ self_assessment: '', self_rating: '' });
  const [turnaround, setTurnaround] = useState(null);

  const dateFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - parseInt(period));
    return d.toISOString().slice(0, 10);
  }, [period]);
  const dateTo = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { date_after: dateFrom, date_before: dateTo };
      const [summaryData, goalsData, reviewsData, activitiesData, turnaroundData] = await Promise.all([
        fetchActivitySummary(authAxios, params).catch(() => null),
        fetchMyGoals(authAxios).catch(() => []),
        listPerformanceReviews(authAxios, { employee: currentUser?.id }).catch(() => []),
        fetchMyActivities(authAxios, params).catch(() => []),
        authAxios.get('/performance-goals/task-turnaround/', { params: { date_from: dateFrom, date_to: dateTo } }).then(r => r.data).catch(() => null),
      ]);
      setSummary(summaryData);
      setGoals(Array.isArray(goalsData) ? goalsData : []);
      setReviews(Array.isArray(reviewsData) ? reviewsData : []);
      setActivities(Array.isArray(activitiesData) ? activitiesData : []);
      setTurnaround(turnaroundData);
    } catch { showFormMessage('error', 'Failed to load performance data.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, dateFrom, dateTo, currentUser]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ---- Handlers ---- */
  const handleActualValueUpdate = async (id, value) => {
    try {
      await updatePerformanceGoal(authAxios, id, { actual_value: value });
      showFormMessage('success', 'Progress updated.');
      const data = await fetchMyGoals(authAxios).catch(() => []);
      setGoals(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to update progress.'); }
  };

  const handleSelfAssessSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitSelfAssessment(authAxios, showSelfAssess.id, selfAssessData);
      showFormMessage('success', 'Self-assessment submitted.');
      setShowSelfAssess(null);
      const data = await listPerformanceReviews(authAxios, { employee: currentUser?.id }).catch(() => []);
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed.'); }
  };

  /* ---- Derived data ---- */
  const totalHours = useMemo(() => activities.reduce((s, a) => s + parseFloat(a.hours || 0), 0), [activities]);
  const billableHours = useMemo(() =>
    activities.filter(a => a.category === 'BILLABLE_WORK').reduce((s, a) => s + parseFloat(a.hours || 0), 0),
  [activities]);
  const utilization = totalHours > 0 ? ((billableHours / totalHours) * 100).toFixed(0) : 0;

  // Weekly hours bar chart data
  const weeklyData = useMemo(() => {
    const map = {};
    activities.forEach(a => {
      const d = new Date(a.date + 'T00:00:00');
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().slice(0, 10);
      if (!map[key]) map[key] = { week: key, billable: 0, nonBillable: 0 };
      const hrs = parseFloat(a.hours || 0);
      if (a.category === 'BILLABLE_WORK') { map[key].billable += hrs; }
      else { map[key].nonBillable += hrs; }
    });
    return Object.values(map).sort((a, b) => a.week.localeCompare(b.week)).map(w => ({
      ...w,
      label: new Date(w.week + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    }));
  }, [activities]);

  // Category pie data
  const categoryData = useMemo(() => {
    const map = {};
    activities.forEach(a => {
      const cat = a.category_display || a.category;
      map[cat] = (map[cat] || 0) + parseFloat(a.hours || 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value * 10) / 10 }))
      .sort((a, b) => b.value - a.value);
  }, [activities]);

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading performance data...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">My Performance</h2>
          <p className="text-sm text-gray-500 mt-1">Track your productivity, goals, and reviews</p>
        </div>
        <div>
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Total Hours</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{totalHours.toFixed(1)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Billable Hours</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{billableHours.toFixed(1)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Utilization</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{utilization}%</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div className="bg-blue-500 rounded-full h-2 transition-all" style={{ width: `${Math.min(utilization, 100)}%` }} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Activities Logged</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">{activities.length}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Weekly Hours Bar */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Weekly Hours</h3>
          {weeklyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="billable" stackId="a" fill="#22c55e" name="Billable" radius={[0, 0, 0, 0]} />
                <Bar dataKey="nonBillable" stackId="a" fill="#eab308" name="Non-Billable" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No data for this period</p>
          )}
        </div>

        {/* Category Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Time by Category</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {categoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No data for this period</p>
          )}
        </div>
      </div>

      {/* Task Turnaround Metrics */}
      {turnaround && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Task Turnaround</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase">Avg Turnaround</p>
              <p className="text-xl font-bold text-indigo-600">{turnaround.firm_avg_turnaround_hours}h</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase">Completed</p>
              <p className="text-xl font-bold text-green-600">{turnaround.firm_total_completed}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase">Overdue</p>
              <p className="text-xl font-bold text-red-600">{turnaround.firm_overdue_count}</p>
            </div>
          </div>
          {turnaround.per_user?.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="pb-2">Team Member</th>
                  <th className="pb-2 text-right">Avg Turnaround</th>
                  <th className="pb-2 text-right">Completed</th>
                  <th className="pb-2 text-right">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {turnaround.per_user.map(u => (
                  <tr key={u.user_id} className="border-b border-gray-100">
                    <td className="py-2 font-medium">{u.name}</td>
                    <td className="py-2 text-right">{u.avg_turnaround_hours}h</td>
                    <td className="py-2 text-right">{u.total_completed}</td>
                    <td className="py-2 text-right text-red-600">{u.overdue_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Goals Section */}
      <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">My Goals</h3>
        {goals.length === 0 ? (
          <p className="text-sm text-gray-400">No goals assigned yet.</p>
        ) : (
          <div className="space-y-3">
            {goals.map(g => {
              const progress = g.progress_pct != null ? g.progress_pct : (g.target_value && g.actual_value
                ? Math.round((parseFloat(g.actual_value) / parseFloat(g.target_value)) * 100)
                : null);
              return (
                <div key={g.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-800">{g.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${GOAL_STATUS_COLORS[g.status] || GOAL_STATUS_COLORS.NOT_STARTED}`}>
                          {g.status_display || g.status}
                        </span>
                      </div>
                      {g.description && <p className="text-xs text-gray-500 mt-1">{g.description}</p>}
                    </div>
                    {g.due_date && (
                      <span className="text-xs text-gray-400 shrink-0">Due: {new Date(g.due_date).toLocaleDateString()}</span>
                    )}
                  </div>
                  {progress != null && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        <span>Progress:</span>
                        <input type="number" step="0.01" min="0"
                          className="border rounded px-2 py-1 w-20 text-xs"
                          value={inlineActual[g.id] !== undefined ? inlineActual[g.id] : (g.actual_value || '')}
                          onChange={e => setInlineActual(p => ({ ...p, [g.id]: e.target.value }))}
                          placeholder="0"
                        />
                        <span>/ {g.target_value} {g.unit}</span>
                        {inlineActual[g.id] !== undefined && inlineActual[g.id] !== (g.actual_value || '').toString() && (
                          <button onClick={() => { handleActualValueUpdate(g.id, inlineActual[g.id]); setInlineActual(p => { const n = {...p}; delete n[g.id]; return n; }); }}
                            className="text-xs text-blue-600 hover:underline font-medium">Save</button>
                        )}
                        <span className="ml-auto">{Math.min(progress, 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all ${ progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-blue-500' : 'bg-yellow-500' }`}
                          style={{ width: `${Math.min(progress, 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Active Reviews */}
      <div className="bg-white rounded-xl shadow-sm border p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">My Reviews</h3>
        {reviews.length === 0 ? (
          <p className="text-sm text-gray-400">No performance reviews yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="pb-2 pr-4">Review Cycle</th>
                  <th className="pb-2 pr-4">Reviewer</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Self Rating</th>
                  <th className="pb-2 pr-4">Manager Rating</th>
                  <th className="pb-2 pr-4">Overall</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reviews.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="py-3 pr-4 font-medium text-gray-700">{r.review_cycle_name || `Cycle #${r.review_cycle}`}</td>
                    <td className="py-3 pr-4 text-gray-600">{r.reviewer_name || '—'}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REVIEW_STATUS_COLORS[r.status] || ''}`}>
                        {r.status_display || r.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4">{r.self_rating ? `${r.self_rating}/5` : '—'}</td>
                    <td className="py-3 pr-4">{r.manager_rating ? `${r.manager_rating}/5` : '—'}</td>
                    <td className="py-3 pr-4">{r.overall_rating ? `${r.overall_rating}/5` : '—'}</td>
                    <td className="py-3">
                      {r.status === 'PENDING_SELF' && (
                        <button onClick={() => {
                          setShowSelfAssess(r);
                          setSelfAssessData({ self_assessment: r.self_assessment || '', self_rating: r.self_rating || '' });
                        }} className="text-xs text-blue-600 hover:underline font-medium">Self-Assess →</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Self-Assessment Quick Modal */}
      {showSelfAssess && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Self-Assessment — {showSelfAssess.review_cycle_name || 'Review'}</h3>
              <form onSubmit={handleSelfAssessSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Self-Assessment *</label>
                  <textarea rows={5} required value={selfAssessData.self_assessment}
                    onChange={e => setSelfAssessData(p => ({ ...p, self_assessment: e.target.value }))}
                    className="border rounded-lg px-3 py-2 text-sm w-full"
                    placeholder="Describe your performance during this review period..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Self-Rating (1-5) *</label>
                  <select required value={selfAssessData.self_rating}
                    onChange={e => setSelfAssessData(p => ({ ...p, self_rating: e.target.value }))}
                    className="border rounded-lg px-3 py-2 text-sm w-full">
                    <option value="">Select</option>
                    {[1,2,3,4,5].map(n => (
                      <option key={n} value={n}>{n} — {['Needs Improvement','Below Expectations','Meets Expectations','Exceeds Expectations','Outstanding'][n-1]}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowSelfAssess(null)}
                    className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit"
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">Submit</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyPerformance;
